/**
 * The freelancer's Available Commissions board (issue #223), kept out of the
 * Lit component so it can be tested. The API answers the open commissions
 * already in board order: by their spec's milestone, soonest target date first,
 * those in no milestone last, and by priority then date posted within each.
 */

/** The section commissions in no milestone fall under. */
export const OTHER_SECTION_NAME = 'Other';

/**
 * @typedef {Object} AvailableCommission
 * @property {string} id
 * @property {string|null} [milestone_id]
 * @property {string|null} [milestone_name]
 * @property {string|null} [milestone_target_date] - YYYY-MM-DD
 * @property {string|null} [milestone_description]
 */

/**
 * @typedef {Object} MilestoneSection
 * @property {string|null} id - the milestone's id; null for "Other"
 * @property {string} name
 * @property {string|null} targetDate - YYYY-MM-DD
 * @property {string|null} description
 * @property {AvailableCommission[]} commissions
 */

/**
 * Cuts the board's commissions into one section per milestone, keeping the
 * API's order, with the commissions in no milestone in an "Other" section last.
 * A milestone with no commissions has no section.
 * @param {AvailableCommission[]} commissions
 * @returns {MilestoneSection[]}
 */
export function groupByMilestone(commissions) {
    /** @type {Map<string, MilestoneSection>} */
    const sections = new Map();
    /** @type {MilestoneSection|null} */
    let other = null;
    for (const c of commissions) {
        if (!c.milestone_id) {
            other ??= {
                id: null,
                name: OTHER_SECTION_NAME,
                targetDate: null,
                description: null,
                commissions: [],
            };
            other.commissions.push(c);
            continue;
        }
        let section = sections.get(c.milestone_id);
        if (!section) {
            section = {
                id: c.milestone_id,
                name: c.milestone_name || '',
                targetDate: c.milestone_target_date || null,
                description: c.milestone_description || null,
                commissions: [],
            };
            sections.set(c.milestone_id, section);
        }
        section.commissions.push(c);
    }
    return other ? [...sections.values(), other] : [...sections.values()];
}

const DATE_LABEL = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
});

/**
 * A milestone's target date (YYYY-MM-DD) as "Nov 1, 2026". It's a calendar
 * date, not a moment, so it's read and shown in UTC: parsed as local time, it
 * would show the day before west of UTC.
 * @param {string|null|undefined} date
 * @returns {string}
 */
export function milestoneDateLabel(date) {
    if (!date) return '';
    return DATE_LABEL.format(new Date(`${date}T00:00:00Z`));
}
